import { useEffect, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { Avatar, Btn, Dropdown, Icon, IconBtn, Input, MoreMenu, Pill, Spinner, Tab, T, alpha } from "@v0y4ge/jrni-ui";
import { StatTile } from "../../../shell/composites";
import { useViewport } from "../../../shell/useResponsive";
import { can, isAdmin } from "../../../shell/access";
import { AuditTrailDrawer } from "../../../shell/AuditTrail";
import { useNav } from "../../../shell/router";
import { eventActions } from "../eventActions";
import { CancelEventConfirm } from "../CancelEventConfirm";
import { EventLifecycleConfirm, lifecycleItems, type LifecycleKind } from "../EventLifecycle";
import { SHARE_SOURCES } from "../../../lib/public-url";
import { formatTicketMoney, useEventTicketing, type EventTicket, type EventTicketsState } from "./event-tickets";
import { LoadingRegion, SkeletonBox } from "../../../lib/loading-skeletons";
import { moduleKnownAbsent, useTenantModules } from "../../../lib/tenant-modules";
import { EventShareTab } from "./EventShareTab";
import { hostPickerOptions, hostSelectedValue, hostUpdateInput } from "./event-host";
import { checkInWindow, toUIEvent, type UIEvent } from "../../../lib/events-adapter";
import { useT } from "../../../lib/i18n";
import { checkInGuest, creation, approveGuestReservation, declineGuestReservation, listEventCapacityCommitments, listEventDutyShifts, assignEventDuty, removeEventDuty, promoteWaitlistGuest, promoteWaitlistBatch } from "@v0y4ge/api-client";
import { tv2Call } from "../../tickets/v2/tickets-v2-api";
import { upsertEvent, refetchEvents } from "../useEvents";
import { useStaff } from "../../staff/useStaff";
import { toast } from "../../../shell/toast";
import { useGuests, type Guest } from "../../guests/useGuests";
import { GuestAnswers } from "../../guests/GuestAnswers";
import { useCustomerNoun, useIcu } from "../../customers/customers-shared";

/* ── Small drawer-local layout helpers (verbatim from the prototype) ─────── */
function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h4 style={{ margin: "0 0 4px", fontSize: 12, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.04 }}>{title}</h4>
      <div>{children}</div>
    </div>
  );
}
function KV({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
      <span style={{ color: T.soft }}>{k}</span>
      <span style={{ color: T.n1, fontWeight: 500, textAlign: "right" }}>{v}</span>
    </div>
  );
}

/* Quick settings (Public / Auto-confirm / Allow +1) were removed on 2026-10-04 (CC): an honest-disabled
   placeholder since 2026-07-25 with no op behind it, every switch hardcoded OFF and badged "Pending op" —
   so "Public" read OFF on a public event. Visibility is set in the event designer, and reservations
   have a real switch in the Registrations banner. */

/* ── Host (event organiser) ───────────────────────────────────────────────
 *
 * Editable here because this kit carries no event-creation wizard — the
 * Details step that used to own the Host dropdown is not part of the composed
 * source, so the detail drawer is the only surface an operator can reach.
 *
 * WHAT TRAVELS IS THE STAFF ID. The event's host is `staffId` — the `id` of a
 * member from `listStaff` — and the events worker verifies it and resolves the
 * canonical `staffName` from the member (StudioEventInputBase.staffId,
 * api-contracts/src/events.ts). Option values are ids, never names; the rules
 * (duplicate names, legacy labels, explicit clear) live in ./event-host.ts.
 *
 * A host the active staff list does not carry is kept rather than rewritten or
 * blanked: a LINKED host (a member since deactivated) stays as its own option;
 * a LEGACY label with no id stays visibly unlinked and is never matched to a
 * member by name — picking a member, even one with that same name, is what
 * sets the id.
 *
 * `ev` is the snapshot `nav.openEvent` was handed, so the saved row is held
 * here: the update RESPONSE mapped through `toUIEvent`, never a name assembled
 * locally.
 */
function HostRow({ ev }: { ev: UIEvent }) {
  const t = useT();
  const { members } = useStaff();
  /* An ended event is read-only (the Archive keeps its reporting): the read-only line, as for a viewer. */
  const writable = can("events.write") && ev.status !== "past";
  const [saved, setSaved] = useState<UIEvent | null>(null);
  const [busy, setBusy] = useState(false);
  // A fresh row (the drawer reopened, or another surface's edit) wins over a held response.
  useEffect(() => { setSaved(null); }, [ev]);
  const row = saved && saved.id === ev.id ? saved : ev;
  /* `—` is the adapter's placeholder for an absent staffName, not a value. */
  const hostName = row.host && row.host !== "—" ? row.host : "";
  const hostId = row.hostId ?? null;

  if (!writable) return <KV k={t("events.detailOverlay.host", "Host")} v={row.host} />;

  const value = hostSelectedValue(hostId, hostName);
  const options = hostPickerOptions({
    hostId, hostName, members,
    noHostLabel: t("events.detailOverlay.noHost", "No host"),
    // No catalog key covers this yet — suggested key below; extraction is downstream.
    unlinkedHint: t("events.detailOverlay.hostNotLinked", "not linked to staff"),
  });

  const commit = (next: string) => {
    /* The chosen member's own name travels with the id, so the row's label and
       its link cannot disagree after a save (CC, 2026-09-22). */
    const picked = (members as any[]).find((m) => m && m.id === next);
    const input = hostUpdateInput(value, next, picked ? (picked.name || picked.email || "") : "");
    if (!input) return;
    setBusy(true);
    void creation.updateEvent(row.id, input)
      .then((dto) => {
        const fresh = toUIEvent(dto);
        setSaved(fresh);
        upsertEvent(fresh);
        toast({ title: input.staffId
          ? t("events.detailOverlay.hostSaved", "Host updated")
          : t("events.detailOverlay.hostCleared", "Host cleared") });
      })
      .catch((e) => {
        console.warn("updateEvent(staffId) failed:", e);
        /* A rejected promise here is NOT proof the write was refused: the client
           parses the response through `StudioEventDto`, so a 200 whose body is
           shaped differently rejects too. Re-read rather than trusting the
           local revert — the server row is the only thing that knows. */
        void refetchEvents();
        toast({ title: t("events.detailOverlay.hostSaveFailed", "Couldn't change the host"),
          subtitle: t("events.detailOverlay.nothingWasSaved", "Nothing was saved — retry in a moment."), tone: "danger" });
      })
      .finally(() => setBusy(false));
  };

  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, fontSize: 13, padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
      <span style={{ color: T.soft }}>{t("events.detailOverlay.host", "Host")}</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        {hostName ? <Avatar size={20} initials={hostName.split(" ").map((w) => w[0]).join("").slice(0, 2)} color={ev.hue} /> : null}
        <Dropdown options={options} value={value} onChange={commit} disabled={busy}
          aria-label={t("events.detailOverlay.host", "Host")} style={{ minWidth: 200 }} />
      </span>
    </div>
  );
}

/**
 * Assigned staff (2026-10-02, Greg) — the people working the event besides the
 * host. Each one's calendar is blocked for the event and it shows on their My Day
 * (server side; REQ cc-outbox/2026-10-02-event-assigned-staff.md). Saves the whole
 * list through `updateEvent({ assignedStaffIds })` and shows what the server sent
 * back; a response without the list is reported, not assumed.
 * Suggested keys: events.detailOverlay.staff, .addStaff, .removeStaff, .staffHint,
 * .staffSaved, .staffNotConfirmed, .staffNotEchoed, .staffSaveFailed.
 */
/* STAFF (2026-10-02): ONE staffing control. Event staffing = workforce event-duty shifts
   (operator ruling) — each person gets a published shift (source "event") on their roster, it
   counts toward hours, and My Day reads the same rows. Data: bound — listEventDutyShifts /
   assignEventDuty / removeEventDuty. Replaces the half-bound updateEvent({ assignedStaffIds })
   write, which the serving path never echoed, and the separate "Event staff" section. */
function StaffRow({ ev }: { ev: UIEvent }) {
  const t = useT();
  const { members } = useStaff();
  /* Adding and removing staff are assignEventDuty / removeEventDuty, which the server allows on
     events.write for this event (CC, 2026-10-04). schedules.write alone cannot make them succeed,
     so it gets the read-only line rather than controls that always fail. */
  /* An ended event is read-only (the Archive keeps its reporting): the read-only line, as for a viewer. */
  const writable = can("events.write") && ev.status !== "past";
  const [shifts, setShifts] = useState<Array<{ id: string; staffId: string | null; staffName: string | null; role: string; start: string; end: string }> | null>(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [role, setRole] = useState<"Event Manager" | "Event Coordinator" | "Host">("Event Coordinator"); // ui-local
  const load = () => listEventDutyShifts(ev.id).then((r) => { setShifts(r.shifts as any); setErr(false); }).catch(() => setErr(true));
  useEffect(() => { void load(); }, [ev.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const label = t("events.detailOverlay.staff", "Staff");
  const list = shifts || [];
  const roleLabel = (r: string) => r === "Event Manager" ? t("shell.access.eventManager", "Event Manager") : r === "Event Coordinator" ? t("shell.access.eventCoordinator", "Event Coordinator") : t("events.staff.host", "Host");
  if (!writable) return <KV k={label} v={err ? "—" : list.length ? list.map((x) => `${x.staffName} (${roleLabel(x.role)})`).join(", ") : "—"} />;
  const add = async (staffId: string) => {
    setBusy(true);
    try { await assignEventDuty(ev.id, { staffId, role }); toast({ tone: "success", title: t("events.detailOverlay.staffSaved", "Staff updated"), subtitle: t("events.staff.onRoster", "On their roster as an event duty shift") }); void load(); }
    catch (e: any) { toast({ title: t("events.detailOverlay.staffSaveFailed", "Couldn't change the staff"), subtitle: String(e?.message || e), tone: "danger" }); }
    finally { setBusy(false); }
  };
  const remove = async (id: string) => {
    setBusy(true);
    try { await removeEventDuty(ev.id, id); setShifts((cur) => (cur ? cur.filter((x) => x.id !== id) : cur)); toast({ tone: "success", title: t("events.detailOverlay.staffSaved", "Staff updated") }); void load(); }
    catch (e: any) { toast({ title: t("events.detailOverlay.staffSaveFailed", "Couldn't change the staff"), subtitle: String(e?.message || e), tone: "danger" }); }
    finally { setBusy(false); }
  };
  const taken = new Set(list.filter((x) => x.role === role).map((x) => x.staffId));
  const addable = (members as any[]).filter((m) => m && m.id && (m.status ?? "active") === "active" && !taken.has(m.id) && (m.name || m.email));
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, fontSize: 13, padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
      <span style={{ color: T.soft, paddingTop: 6 }}>{label}</span>
      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, minWidth: 0 }}>
        {err ? <span style={{ fontSize: 12, color: T.soft }}>{t("events.staff.loadError", "Couldn't load the event staff.")}</span> : null}
        {list.length ? (
          <span style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-end" }}>
            {list.map((x) => (
              <span key={x.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 2px 2px 4px", borderRadius: 999, background: alpha(T.p1, 0.08) }}>
                <Avatar size={18} initials={(x.staffName || "?").split(" ").map((w) => w[0]).join("").slice(0, 2)} color={ev.hue} />
                <span style={{ fontWeight: 600, color: T.n1 }}>{x.staffName}</span>
                <span style={{ color: T.soft }}>· {roleLabel(x.role)}</span>
                <IconBtn label={t("events.detailOverlay.removeStaff", "Remove {name}", { name: x.staffName || x.id })} disabled={busy} onClick={() => remove(x.id)}><Icon.x size={12} /></IconBtn>
              </span>
            ))}
          </span>
        ) : null}
        <span style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Dropdown options={[{ value: "Event Manager", label: roleLabel("Event Manager") }, { value: "Event Coordinator", label: roleLabel("Event Coordinator") }, { value: "Host", label: roleLabel("Host") }]}
            value={role} onChange={(v: string) => setRole(v as any)} aria-label={t("events.staff.role", "Role")} />
          <Dropdown options={[{ value: "", label: t("events.detailOverlay.addStaff", "Add staff…") }, ...addable.map((m) => ({ value: m.id, label: m.name || m.email }))]}
            value="" onChange={(v: string) => { if (v) void add(v); }} disabled={busy || !addable.length}
            aria-label={t("events.detailOverlay.addStaff", "Add staff…")} style={{ minWidth: 200 }} />
        </span>
        <span style={{ fontSize: 11.5, color: T.soft, textAlign: "right", maxWidth: 260 }}>{t("events.detailOverlay.staffHintDuty", "Each person gets an event duty shift on their roster — it counts toward their hours and shows on their My Day.")}</span>
      </span>
    </div>
  );
}

/* ── Overview tab ─────────────────────────────────────────────────────── */
function EventOverviewTab({ ev, tickets }: { ev: UIEvent; tickets: EventTicket[] }) {
  const t = useT();
  const nav = useNav();
  const phone = useViewport() === "phone";
  // LOCAL PATCH (platform side, 2026-09-22): the API may carry the legacy
  // camelCase alias, while the generated UIEvent type declares location_id.
  const raw = ev.raw as typeof ev.raw & { locationId?: unknown };
  const locationId = String(raw.locationId ?? raw.location_id ?? "").trim();
  const locationValue = can("locations.read") && locationId
    ? <Btn variant="link" size="sm" onClick={() => { nav.closeEvent(); nav.go("locations.edit", { id: locationId, view: 1 }); }} style={{ height: "auto", padding: 0 }}>{ev.location}</Btn>
    : ev.location;
  /* Every tier that sold seats, retired ones included: a retired tier sells no more, but what it sold
     stays sold (CC, 2026-10-04). Amounts are in the event's own currency. */
  const paid = tickets.filter((t) => t.price > 0);
  const currency = tickets[0]?.currency ?? "USD";
  const revenue = paid.reduce((sum, t) => sum + t.price * t.sold, 0);
  const utilPct = ev.capacity ? Math.round((ev.registered / ev.capacity) * 100) : 0;
  // Addendum item 4: the "+N today" delta was hash-derived (charCodeAt % 9) — a
  // generated value presented as measured; no DTO field backs a daily delta, so
  // the tile shows real utilisation instead.
  const expected = Math.max(0, ev.registered - ev.checked);
  // The Registered tile counts confirmed guests, the same people the Registrations tab badge counts;
  // pending approval requests hold a seat, so capacity use stays on `registered` (CC, 2026-10-06).
  const confirmedCount = ev.confirmed ?? ev.registered;
  const thirdTile = revenue > 0
    ? <StatTile label={t("events.detailOverlay.revenue", "Revenue")} value={formatTicketMoney(revenue, currency)} delta={`${paid.reduce((n, t) => n + t.sold, 0)} paid`} accent={T.orange1} icon={<Icon.ticket size={14} />} />
    : ev.waitlist > 0
      ? <StatTile label={t("events.detailOverlay.waitlist", "Waitlist")} value={`${ev.waitlist}`} delta="Ready to promote" accent={T.tPurple} icon={<Icon.users size={14} />} />
      // Capacity 0 = unlimited (CC 0030 item 16): no percentage, no seats-left count.
      : <StatTile label={t("events.detailOverlay.capacity", "Capacity")} value={ev.capacity ? `${utilPct}%` : t("events.detailOverlay.unlimited", "Unlimited")} delta={ev.capacity ? `${Math.max(0, ev.capacity - ev.registered)} seats left` : t("events.detailOverlay.noLimit", "No seat limit")} accent={T.p1} icon={<Icon.ticket size={14} />} />;
  return (
    <>
      {/* Phone: tiles wrap instead of cutting the third one off (CC, 2026-10-05). */}
      <div style={{ display: "grid", gridTemplateColumns: phone ? "repeat(auto-fit, minmax(104px, 1fr))" : "1fr 1fr 1fr", gap: 12 }}>
        <StatTile label={t("events.detailOverlay.registered", "Registered")} value={`${confirmedCount}`} delta={ev.capacity ? `${utilPct}% of cap` : t("events.detailOverlay.noLimit", "No seat limit")} icon={<Icon.users size={14} />} />
        <StatTile label={t("events.detailOverlay.checkedIn", "Checked in")} value={`${ev.checked}`} delta={confirmedCount ? `${Math.round((ev.checked / confirmedCount) * 100)}% of registered` : "—"} accent={T.green1} icon={<Icon.check size={14} />} />
        {thirdTile}
      </div>
      <DetailSection title={t("events.detailOverlay.when", "When")}><KV k="Date" v={ev.date} /><KV k="Time" v={ev.time && ev.zone ? `${ev.time} ${ev.zone}` : ev.time} /></DetailSection>
      <DetailSection title={t("events.detailOverlay.where", "Where")}><KV k="Location" v={locationValue} /><HostRow ev={ev} /><StaffRow ev={ev} /><KV k="Capacity" v={`${ev.capacity ? `${ev.capacity} seats` : t("events.detailOverlay.unlimited", "Unlimited")}${ev.waitlist > 0 ? ` · ${ev.waitlist} waitlist` : ""}`} /></DetailSection>
      <DetailSection title={t("events.detailOverlay.capacity", "Capacity")}>
        <div style={{ height: 10, background: T.n5, borderRadius: 999, overflow: "hidden", display: "flex" }}>
          <div style={{ height: "100%", width: `${ev.capacity ? (ev.checked / ev.capacity) * 100 : 0}%`, background: T.green1 }} />
          <div style={{ height: "100%", width: `${ev.capacity ? Math.max(0, ((ev.registered - ev.checked) / ev.capacity) * 100) : 0}%`, background: T.p1 }} />
        </div>
        <div style={{ fontSize: 12, color: T.soft, marginTop: 6 }}>{ev.checked} {t("events.detailOverlay.checkedIned949840", "checked in ·")}{expected} {t("events.detailOverlay.expected", "expected ·")}{ev.waitlist} {t("events.detailOverlay.onWaitlist", "on waitlist")}</div>
      </DetailSection>
    </>
  );
}

/* ── Tickets tab ──────────────────────────────────────────────────────── */
function EventTicketsTab({ ticketing }: { ticketing: EventTicketsState & { retry: () => void } }) {
  const t = useT();
  const palette = [T.tLightBlue, T.tPurple, T.tPink, T.tOrange, T.tGreen, T.tTurquoise];
  if (ticketing.status === "loading") {
    return (
      <LoadingRegion label={t("events.detailOverlay.ticketsLoading", "Loading this event's tickets…")} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {[0, 1, 2].map((i) => <SkeletonBox key={i} height={62} radius={6} />)}
      </LoadingRegion>
    );
  }
  if (ticketing.status === "error") {
    return (
      <div role="alert" style={{ padding: 14, border: `1px solid ${T.border}`, borderRadius: 6, display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ flex: 1, fontSize: 13, color: T.n2 }}>{t("events.detailOverlay.ticketsLoadFailed", "Couldn't load this event's tickets.")}</span>
        <Btn size="sm" variant="secondary" onClick={ticketing.retry}>{t("common.actions.retry", "Retry")}</Btn>
      </div>
    );
  }
  const tickets = ticketing.tickets.filter((x) => !x.retired);
  if (tickets.length === 0) {
    return <div style={{ padding: "24px", textAlign: "center", color: T.soft, fontSize: 12.5 }}>{t("events.detailOverlay.noTicketsYet", "This event has no tickets yet.")}</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {tickets.map((tk, i) => (
        <div key={tk.id} style={{ padding: 14, border: `1px solid ${T.border}`, borderRadius: 6, display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ width: 4, alignSelf: "stretch", background: palette[i % palette.length], borderRadius: 999 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.n1 }}>{tk.name}</div>
            <div style={{ fontSize: 12, color: T.soft }}>{tk.desc}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{tk.sold} <span style={{ color: T.soft, fontWeight: 400 }}>/ {tk.capacity}</span></div>
            <div style={{ fontSize: 11, color: T.soft }}>{tk.price === 0 ? t("events.detailOverlay.text", "Free") : formatTicketMoney(tk.price, tk.currency)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Registrations tab ─────────────────────────────────────────────────
 * The event's REAL roster — same data source AND the same Check-in action as
 * Event Day: useGuests + checkInGuest, GET/PATCH /v1/admin/events/:id/guests
 * via the typed client. Previously this tab called a separate, differently-
 * shaped /registrations route with no serving-engine binding — it 404'd, and
 * a bug in the tab's own effect (a `live`-flag closure raced against the
 * fast rejection so the failure was swallowed) left it stuck on "Loading…"
 * forever instead of showing the honest empty/error state Event Day always
 * has. Reusing useGuests fixes both: same proven data path, same real
 * check-in action, no separate fetch to get wrong.
 *
 * VOCABULARY (2026-09-14, operator): "guest" is retired — there are only
 * customers, and the tenant names them ("Students" in Capture). The wire
 * (`useGuests`, `checkInGuest`, the /guests route) keeps its names; only what
 * an operator reads changes, and it reads the tenant's own noun. */
function EventGuestsTab({ ev }: { ev: UIEvent }) {
  const t = useT();
  const icu = useIcu();
  const { nounPlural } = useCustomerNoun();
  const nav = useNav();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [checkingId, setCheckingId] = useState<string | null>(null);
  /* The registrant whose answers are open, on any day (operator ruling 28 Sept, CC 0058). */
  const [openId, setOpenId] = useState<string | null>(null);
  const { guests, loading, reload } = useGuests({ eventId: ev.id, limit: 200 });
  /* The Customers directory is served by the booking module (CC, 2026-10-04); on a tenant that is KNOWN
     to lack it, the link would open a screen whose read is refused. Unknown keeps the link. */
  const customersOff = moduleKnownAbsent("booking", useTenantModules());
  /* An archived event is read-only here (CC, 2026-10-14): the server refuses check-in, approval, waitlist moves
     and the reservations switch on it, so none is offered and each write below refuses it too. Read from the
     event's own stored status: the adapter maps every ended event to "past", and the check-in window deliberately
     stays open after the start day so an event that is not archived can still take a late check-in. */
  const isArchived = (ev.raw as { status?: string } | undefined)?.status === "archived";

  /* RESERVATION WORKFLOW (2026-10-02). The event is a reservation event when its ticketing
     carries a requiresApproval invite tier and reservations are not switched off for it — or
     when any registration is still pending (a request made before the switch-off still needs
     a decision). Approve / Decline are awaited ops; nothing changes on screen until they answer. */
  const [tk, setTk] = useState<{ tiers: Array<{ name: string; audience?: string; requiresApproval?: boolean; status?: string }>; reservationsOff?: boolean } | null>(null);
  const [tkErr, setTkErr] = useState<string | null>(null);
  const loadTk = () => tv2Call("GET", `/v1/admin/events/${encodeURIComponent(ev.id)}/ticketing`).then((r: any) => { setTk(r.ticketing); setTkErr(null); }).catch((e: any) => setTkErr(String(e?.message || e)));
  useEffect(() => { void loadTk(); }, [ev.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const approvalTiers = (tk?.tiers || []).filter((x) => x.requiresApproval && x.audience === "invite" && x.status !== "retired");
  const pendingCount = guests.filter((g) => g.status === "pending-approval").length;
  const workflowOn = approvalTiers.length > 0 && !tk?.reservationsOff;
  const isReservationEvent = workflowOn || pendingCount > 0;
  /* Seats held by open reservation commitments (listEventCapacityCommitments). */
  const [heldSeats, setHeldSeats] = useState<number | null>(null);
  useEffect(() => { let live = true; listEventCapacityCommitments(ev.id).then((r) => { if (live) setHeldSeats(r.openSeats); }).catch(() => { if (live) setHeldSeats(null); }); return () => { live = false; }; }, [ev.id, guests.length, pendingCount]);
  const canApprove = can("guests.approve");
  const [decidingId, setDecidingId] = useState<string | null>(null);
  /* While a decision is in flight (CC, 2026-10-04) the clicked row's Approve / Decline show a spinner and are
     disabled, and a ref blocks a second decide() before React re-renders (a fast double click). Both clear when
     the request settles, success or failure; the list refresh is not awaited. */
  const decidingRef = useRef<string | null>(null);
  const [declineFor, setDeclineFor] = useState<string | null>(null); // ui-local
  const [declineReason, setDeclineReason] = useState(""); // ui-local
  const [savingOff, setSavingOff] = useState(false);
  const decide = async (g: Guest, kind: "approve" | "decline") => {
    if (isArchived || decidingRef.current) return;
    decidingRef.current = g.id;
    setDecidingId(g.id);
    try {
      if (kind === "approve") {
        const r = await approveGuestReservation(ev.id, g.id);
        toast({ tone: "success", title: t("events.reservations.approvedToast", "Attendance approved"), subtitle: r.payment === "unpaid" ? t("events.reservations.approvedUnpaid", "{name} · payment not taken — marked unpaid", { name: g.name }) : g.name });
      } else {
        await declineGuestReservation(ev.id, g.id, { reason: declineReason.trim() });
        toast({ tone: "success", title: t("events.reservations.declinedToast", "Request declined"), subtitle: t("events.reservations.declinedSub", "{name} · seat released, no email sent", { name: g.name }) });
        setDeclineFor(null); setDeclineReason("");
      }
      reload();
    } catch (err: any) {
      toast({ tone: "danger", title: kind === "approve" ? t("events.reservations.approveFailed", "Couldn't approve") : t("events.reservations.declineFailed", "Couldn't decline"), subtitle: String(err?.message || err) });
    } finally { decidingRef.current = null; setDecidingId(null); }
  };
  /* The switch writes ONLY reservationsOff (CC, 2026-10-04): the ticketing PUT leaves tiers and currency
     alone when they are omitted, so a tier edit made elsewhere since this screen loaded is never
     overwritten by the copy cached here. */
  const toggleReservations = async () => {
    if (isArchived || !tk) return;
    setSavingOff(true);
    try {
      const r: any = await tv2Call("PUT", `/v1/admin/events/${encodeURIComponent(ev.id)}/ticketing`, { reservationsOff: !tk.reservationsOff });
      setTk(r.ticketing);
      toast({ tone: "success", title: r.ticketing.reservationsOff ? t("events.reservations.offToast", "Reservations off for this event") : t("events.reservations.onToast", "Reservations on for this event") });
    } catch (err: any) {
      toast({ tone: "danger", title: t("events.reservations.toggleFailed", "Couldn't change reservations"), subtitle: String(err?.message || err) });
    } finally { setSavingOff(false); }
  };

  const isCheckedIn = (g: Guest) => g.status === "checked-in";
  /* "All" is the confirmed roster (CC, 2026-10-04, operator decision): registered and checked in, the same
     people the tab badge counts. Waitlisted and waiting-for-approval guests are not confirmed and keep their
     own chips, as do declined requests; a cancelled registration is on no chip. */
  const onRoster = (g: Guest) => g.status === "registered" || g.status === "checked-in";
  const inBucket = (g: Guest) =>
    status === "all" ? onRoster(g)
      : status === "checked-in" ? isCheckedIn(g)
      : status === "waitlist" ? g.status === "waitlist"
      : status === "pending-approval" ? g.status === "pending-approval"
      : status === "declined" ? g.status === "declined"
      : g.status === "registered";
  const needle = q.trim().toLowerCase();
  const matches = (g: Guest) => !needle || [g.name, g.email, g.company, g.ticketLabel, g.ticket].some((v) => typeof v === "string" && v.toLowerCase().includes(needle));
  const shown = guests.filter((g) => inBucket(g) && matches(g));

  const counts: Record<string, number> = {
    all: guests.filter(onRoster).length,
    "checked-in": guests.filter(isCheckedIn).length,
    registered: guests.filter((g) => g.status === "registered").length,
    waitlist: guests.filter((g) => g.status === "waitlist").length,
    "pending-approval": pendingCount,
    declined: guests.filter((g) => g.status === "declined").length,
  };
  /* The last waiting guest moved up (CC 0300): the Waitlist chip goes, so fall back to the confirmed view
     instead of an empty list that reads like a search miss. */
  useEffect(() => { if (status === "waitlist" && !loading && counts.waitlist === 0) setStatus("all"); }, [status, loading, counts.waitlist]);

  const chip = (id: string, label: string) => (
    <Btn key={id} variant="secondary" onClick={() => setStatus(id)} style={{ height: "auto", padding: "4px 10px", borderRadius: 999, border: `1px solid ${status === id ? T.p1 : T.border}`, background: status === id ? T.p1 : T.white, color: status === id ? T.white : T.n2, fontSize: 12, fontWeight: 600, boxShadow: "none" }}>
      {label}<span style={{ opacity: 0.7, fontWeight: 500 }}>{counts[id]}</span>
    </Btn>
  );

  const initialsOf = (name: string) => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase() || "?";

  /* Moving a guest up from the waitlist (CC 0255): one guest into a free place, or the next
     in line. Both confirm the guest exactly as an automatic promotion does, so the "Waitlist
     moved" message goes out when it is switched on. Shown only with waitlist.write; one move
     at a time, and the list is re-read after it. */
  const canPromote = can("waitlist.write");
  /* "Move up next" can't move anyone while every place is taken (CC 0290 item 3). A ticket can still be
     full with places free overall; the server's 409 CAPACITY_FULL covers that case. */
  const noFreePlace = !!ev.capacity && ev.registered >= ev.capacity;
  const [promotingId, setPromotingId] = useState<string | null>(null);
  const promotingRef = useRef<string | null>(null);
  const promote = async (g: Guest | null) => {
    if (isArchived || promotingRef.current) return;
    const key = g ? g.id : "next";
    promotingRef.current = key; setPromotingId(key);
    try {
      if (g) await promoteWaitlistGuest(ev.id, g.id);
      else await promoteWaitlistBatch(ev.id);
      toast({ tone: "success", title: "Moved up from the waitlist", subtitle: g ? `${g.name} now has a place.` : "The next guest in line now has a place." });
      reload();
      /* The Registrations badge reads the events list, so refresh it too (CC 0300). */
      void refetchEvents();
    } catch (err: any) {
      const noPlace = err?.status === 409 || err?.status === 422;
      toast({
        tone: "danger",
        title: g ? `Couldn't move ${g.name} up` : "Couldn't move the next guest up",
        subtitle: noPlace ? "There's no free place to move them into." : err?.status === 403 ? t("eventday.eventDay.err.forbidden", "Your role can't do this for this event.") : String(err?.message || err),
      });
    } finally { promotingRef.current = null; setPromotingId(null); }
  };

  const doCheckIn = async (g: Guest) => {
    if (isArchived) return;
    setCheckingId(g.id);
    try { await checkInGuest(ev.id, g.id); reload(); }
    catch (err: any) {
      /* A failed check-in says why (CC, 2026-10-04); it was only logged, so the button simply did nothing. */
      console.warn("checkInGuest failed:", err);
      toast({
        tone: "danger",
        title: t("events.detailOverlay.checkInFailed", "Couldn't check in {name}", { name: g.name }),
        subtitle: err?.code === "CHECK_IN_TOO_EARLY" || err?.code === "EVENT_CHECKIN_WINDOW_UNAVAILABLE" ? t("eventday.eventDay.err.checkInTooEarly", "Check-in opens on the day of the event.")
          : err?.status === 403 ? t("eventday.eventDay.err.forbidden", "Your role can't do this for this event.")
          : String(err?.message || t("eventday.eventDay.err.retry", "Retry in a moment.")),
      });
    }
    finally { setCheckingId(null); }
  };

  const body = () => {
    if (loading && guests.length === 0) {
      return <div style={{ padding: "24px", textAlign: "center", color: T.soft, fontSize: 12.5 }}>{t("common.states.loading", "Loading…")}</div>;
    }
    if (shown.length === 0) {
      return (
        <div style={{ padding: "24px", textAlign: "center", color: T.soft, fontSize: 12.5 }}>
          {guests.length === 0
            ? t("events.detailOverlay.noRegistrationsYet", "No one has registered for this event yet.")
            : t("events.detailOverlay.noRegistrationsMatch", "No registrations match this search.")}
        </div>
      );
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
        {shown.map((g, i) => (
          <div key={g.id} style={{ borderTop: i === 0 ? "none" : `1px solid ${T.border}`, background: T.white }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px" }}>
            <Avatar initials={initialsOf(g.name)} alt={g.name} size={26} />
            <button type="button" aria-expanded={openId === g.id} onClick={() => setOpenId((cur) => (cur === g.id ? null : g.id))}
              title={t("events.detailOverlay.showAnswers", "Show answers")}
              style={{ minWidth: 0, flex: 1, textAlign: "left", background: "transparent", border: 0, padding: 0, cursor: "pointer", fontFamily: "inherit" }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.name}</div>
              <div style={{ fontSize: 11.5, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.ticketLabel || g.ticket || g.email}{g.company ? t("events.detailOverlay.text5", "· {company}", { company: g.company }) : ""}</div>
            </button>
            <Icon.chev size={10} dir={openId === g.id ? "down" : "right"} />
            {g.status === "pending-approval" ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <Pill tone="warn">{t("events.reservations.pending", "Pending approval")}</Pill>
                {canApprove && !isArchived ? <>
                  <Btn size="sm" disabled={decidingId === g.id} iconLeft={decidingId === g.id && declineFor !== g.id ? <Spinner size="sm" onColor /> : undefined} onClick={() => decide(g, "approve")}>{t("events.reservations.approve", "Approve")}</Btn>
                  <Btn size="sm" variant="secondary" disabled={decidingId === g.id} onClick={() => { setDeclineFor(declineFor === g.id ? null : g.id); setDeclineReason(""); }}>{t("events.reservations.decline", "Decline")}</Btn>
                </> : null}
              </div>
            ) : !isArchived && can("guests.checkin") && checkInWindow(ev) === "open" && g.status !== "waitlist" && g.status !== "cancelled" && g.status !== "declined" ? (
              <Btn variant="secondary" disabled={checkingId === g.id || isCheckedIn(g)} onClick={() => doCheckIn(g)}
                style={{ height: 28, padding: "0 12px", borderRadius: 999, border: `1.5px solid ${isCheckedIn(g) ? T.green1 : T.n4}`, background: isCheckedIn(g) ? T.green2 : T.white, color: isCheckedIn(g) ? T.green1 : T.n2, fontSize: 12, fontWeight: 600, cursor: isCheckedIn(g) ? "default" : "pointer", flexShrink: 0, opacity: checkingId === g.id ? 0.6 : 1, boxShadow: "none" }}>
                {isCheckedIn(g) ? `✓ ${t("events.detailOverlay.checkedIn", "Checked in")}` : checkingId === g.id ? "…" : t("eventday.eventDay.checkIn", "Check in")}
              </Btn>
            ) : g.status === "waitlist" && canPromote && !isArchived ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <Pill tone="warn">{t("events.detailOverlay.waitlist", "Waitlist")}</Pill>
                <Btn size="sm" variant="secondary" data-testid="waitlist-promote" disabled={promotingId !== null}
                  iconLeft={promotingId === g.id ? <Spinner size="sm" /> : undefined} onClick={() => { void promote(g); }}>Move into a place</Btn>
              </div>
            ) : isArchived && isCheckedIn(g) ? (
              <Pill tone="success">{`✓ ${t("events.detailOverlay.checkedIn", "Checked in")}`}</Pill>
            ) : (
              <Pill tone={g.status === "waitlist" ? "warn" : g.status === "declined" ? "danger" : "neutral"}>
                {g.status === "waitlist" ? t("events.detailOverlay.waitlist", "Waitlist") : g.status === "declined" ? t("events.reservations.declined", "Declined") : t("events.detailOverlay.registered", "Registered")}
              </Pill>
            )}
          </div>
          {declineFor === g.id && !isArchived ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "0 12px 10px 48px", flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                <Input full size="sm" placeholder={t("events.reservations.reasonRequired", "Reason (required, staff only)")} aria-required="true" value={declineReason} onChange={(e: ChangeEvent<HTMLInputElement>) => setDeclineReason(e.target.value)} />
              </div>
              <Btn size="sm" variant="secondary" disabled={decidingId === g.id} onClick={() => { setDeclineFor(null); setDeclineReason(""); }}>{t("common.actions.cancel", "Cancel")}</Btn>
              <Btn size="sm" disabled={decidingId === g.id || !declineReason.trim()} iconLeft={decidingId === g.id ? <Spinner size="sm" onColor /> : undefined} onClick={() => decide(g, "decline")} style={{ background: T.red1, borderColor: T.red1 }}>{t("events.reservations.confirmDecline", "Decline request")}</Btn>
            </div>
          ) : null}
          {openId === g.id ? <GuestAnswers eventId={ev.id} guestId={g.id} /> : null}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {!isArchived && (isReservationEvent || approvalTiers.length > 0) ? (
        <div role="status" style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", borderRadius: 10, border: `1px solid ${isReservationEvent && !tk?.reservationsOff ? alpha(T.orange1, 40) : T.border}`, background: isReservationEvent && !tk?.reservationsOff ? alpha(T.orange1, 8) : T.n6 }}>
          <span style={{ color: isReservationEvent && !tk?.reservationsOff ? T.orange1 : T.soft, display: "inline-flex", marginTop: 2 }}><Icon.check size={14} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{workflowOn || (pendingCount > 0 && !tk?.reservationsOff) ? t("events.reservations.bannerTitle", "Reservation event — staff approve each attendee") : t("events.reservations.bannerOffTitle", "Reservations are off for this event")}</div>
            <div style={{ fontSize: 12.5, color: T.n2, marginTop: 2 }}>
              {pendingCount > 0
                ? (canApprove
                  ? t("events.reservations.bannerPendingHeld", "{count} waiting for approval · {held} seats held until you approve or decline.", { count: pendingCount, held: heldSeats ?? pendingCount })
                  : t("events.reservations.bannerPendingHeldView", "{count} waiting for approval · {held} seats held while they are reviewed.", { count: pendingCount, held: heldSeats ?? pendingCount }))
                : workflowOn ? (canApprove
                  ? t("events.reservations.bannerNone", "Nothing waiting. Requests on {tiers} hold a seat until you approve or decline them.", { tiers: approvalTiers.map((x) => x.name).join(", ") })
                  : t("events.reservations.bannerNoneView", "Nothing waiting. Requests on {tiers} hold a seat until they are reviewed.", { tiers: approvalTiers.map((x) => x.name).join(", ") }))
                : t("events.reservations.bannerOffSub", "{tiers} sell like any invite-only ticket on this event.", { tiers: approvalTiers.map((x) => x.name).join(", ") })}
            </div>
          </div>
          {pendingCount > 0 && status !== "pending-approval" ? <Btn size="sm" variant="secondary" onClick={() => setStatus("pending-approval")}>{t("events.reservations.review", "Review")}</Btn> : null}
          {approvalTiers.length > 0 && can("events.write") && !isArchived ? (
            <Btn size="sm" variant="ghost" disabled={savingOff} onClick={toggleReservations}>{tk?.reservationsOff ? t("events.reservations.turnOn", "Turn on for this event") : t("events.reservations.turnOff", "Turn off for this event")}</Btn>
          ) : null}
        </div>
      ) : null}
      {isReservationEvent && !canApprove ? <div style={{ fontSize: 12, color: T.soft }}>{t("events.reservations.approvalsByCoordinators", "Approvals are made by event coordinators.")}</div> : null}{/* (CC, 2026-10-05) */}
      {tkErr ? <div style={{ fontSize: 12, color: T.soft }}>{t("events.reservations.tkError", "Couldn't read this event's ticket setup — reservation status may be incomplete.")}</div> : null}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <Input full size="sm" leading={<Icon.search size={12} />} placeholder={icu("events.detailOverlay.searchRegistrations", "Search the {nounPlural} registered for this event…", { nounPlural: nounPlural.toLowerCase() })} value={q} onChange={(e: ChangeEvent<HTMLInputElement>) => setQ(e.target.value)} />
        {can("reports.export") ? <Btn variant="secondary" size="sm" icon={<Icon.csv size={12} />} onClick={() => eventActions.exportGuestList(ev)}>{t("common.actions.export", "Export")}</Btn> : null}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {/* Labelled "Confirmed", not "All" (CC 0235): it is the confirmed roster by the 4 Oct ruling, and
            "All 1" beside "Waitlist 1" read as if the waitlisted guest were missing. */}
        {chip("all", "Confirmed")}
        {chip("checked-in", "Checked in")}
        {chip("registered", "Registered")}
        {counts.waitlist > 0 ? chip("waitlist", "Waitlist") : null}
        {isReservationEvent ? chip("pending-approval", t("events.reservations.needsApproval", "Needs approval")) : null}
        {counts.declined > 0 ? chip("declined", t("events.reservations.declined", "Declined")) : null}
      </div>
      {status === "waitlist" && canPromote && !isArchived && counts.waitlist > 0 ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: T.soft }}>Guests move up in waitlist order when a place is free.</span>
          <Btn size="sm" variant="secondary" data-testid="waitlist-promote-next" disabled={promotingId !== null || noFreePlace}
            title={noFreePlace ? "Every place is taken. Free a place first, then move the next guest up." : undefined}
            iconLeft={promotingId === "next" ? <Spinner size="sm" /> : undefined} onClick={() => { void promote(null); }}>Move up next</Btn>
        </div>
      ) : null}
      {body()}
      {customersOff ? null : (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Btn variant="secondary" size="sm" icon={<Icon.arrow dir="right" size={13} />} onClick={() => { nav.closeEvent(); nav.go("customers", { eventId: ev.id }); }}>{icu("events.detailOverlay.openInDirectory", "Open in {nounPlural}", { nounPlural })}</Btn>
        </div>
      )}
    </div>
  );
}

/* ── Drawer shell ─────────────────────────────────────────────────────── */
function EventDetailContents({ ev }: { ev: UIEvent }) {
  const t = useT();
  const icu = useIcu();
  const { nounPlural } = useCustomerNoun();
  const nav = useNav();
  const [tab, setTab] = useState<"overview" | "guests" | "tickets" | "share">("overview");
  const [auditOpen, setAuditOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false); // ui-local — cancel/discard confirm
  const [cancelFresh, setCancelFresh] = useState<UIEvent | null>(null); // ui-local — re-read event for a cancel after a "cancel first" archive refusal
  const [lifeKind, setLifeKind] = useState<LifecycleKind | null>(null); // ui-local — archive / restore / reopen confirm
  /* One read of the event's tiers feeds the Tickets tab, its count and the Overview revenue tile. */
  const ticketing = useEventTicketing(ev.id);
  const tickets = ticketing.status === "ready" ? ticketing.tickets : [];
  const activeTickets = tickets.filter((x) => !x.retired);
  const customersOff = moduleKnownAbsent("booking", useTenantModules());
  /* Phone (CC, 2026-10-04): the drawer is full width, the title column may shrink and wrap, and the action
     cluster keeps its size, so the close and more buttons stay on screen; the tabs scroll and the footer wraps.
     The same phone rule as the shared Drawer (shell/Overlay.tsx). Desktop is unchanged. */
  const phone = useViewport() === "phone";
  const tabStyle = phone ? { padding: "10px 10px" } : undefined; // tighter tabs so all four fit (CC, 2026-10-05)
  return (
    <>
      <div style={{ padding: phone ? "14px 14px" : "16px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
          <span style={{ width: 44, height: 44, borderRadius: 8, background: ev.heroImage ? `url(${ev.heroImage}) center/cover` : ev.hue, color: T.white, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 18, overflow: "hidden" }}>
            {ev.heroImage ? null : ev.title.split(" ").map((w) => w[0]).slice(0, 2).join("")}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: T.n1, margin: 0, overflowWrap: "anywhere" }}>{ev.title}</h2>
              {ev.status === "live" ? <Pill tone="success" dot>{t("common.status.live", "Live")}</Pill>
                : ev.status === "canceled" ? <Pill tone="danger">{t("common.status.cancelled", "Canceled")}</Pill>
                : ev.status === "draft" ? <Pill>{t("common.status.draft", "Draft")}</Pill>
                : ev.status === "past" ? <Pill tone="neutral">{t("events.detailOverlay.ended", "Ended")}</Pill>
                : <Pill tone="primary">{t("common.status.upcoming", "Upcoming")}</Pill>}
            </div>
            <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{ev.template}{ev.template && ev.location ? " · " : ""}{ev.location}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          {ev.status === "past"
            ? <IconBtn label={t("events.detailOverlay.endedNoEdit", "This event has ended, so it can no longer be edited")} disabled onClick={() => {}}><Icon.edit size={14} /></IconBtn>
            : <IconBtn label={t("common.actions.edit", "Edit")} onClick={() => eventActions.editEvent(ev, nav)}><Icon.edit size={14} /></IconBtn>}
          <IconBtn label={t("events.detailOverlay.duplicate", "Duplicate")} onClick={() => eventActions.duplicateAsDraft(ev, nav)}><Icon.copy size={14} /></IconBtn>
          <MoreMenu label={t("events.detailOverlay.moreEventActions", "More event actions")} size={36} iconSize={16} width={260} header={ev.title}
            items={[
              /* An archived event's public page is gone (the public event lookup refuses it): no link to it is offered. */
              ...((ev.raw as { status?: string } | undefined)?.status === "archived" ? [] : [
                { icon: <Icon.external size={14} />, label: t("events.detailOverlay.openLandingPage", "Open landing page"), sub: "Opens in a new tab", onClick: () => eventActions.openLandingPage(ev, nav) },
                { icon: <Icon.link size={14} />, label: t("events.detailOverlay.copyPublicUrl", "Copy public URL"), sub: "Share the registration link", onClick: () => eventActions.copyPublicUrl(ev, SHARE_SOURCES.eventOverlay) },
              ]),
              { icon: <Icon.qr size={14} />, label: t("events.detailOverlay.showCheckInQr", "Show check-in QR"), sub: t("events.detailOverlay.comingSoon", "Coming soon"), onClick: () => eventActions.showCheckInQR(ev) },
              "divider",
              { icon: <Icon.layers size={14} />, label: t("events.detailOverlay.saveAsTemplate", "Save as template"), sub: "Reuse this setup for new events", onClick: () => eventActions.saveAsTemplate(ev, nav) },
              { icon: <Icon.copy size={14} />, label: t("events.detailOverlay.duplicateAsDraft", "Duplicate event"), sub: "Create a copy as a draft", onClick: () => eventActions.duplicateAsDraft(ev, nav) },
              ...(can("reports.export") ? [{ icon: <Icon.download size={14} />, label: t("events.detailOverlay.exportRegistrations", "Export registrations"), sub: "CSV of all registrations", onClick: () => eventActions.exportGuestList(ev) }] : []),
              /* Lifecycle actions by state (EventLifecycle.tsx): Delete draft / Cancel (events.delete), Archive (ended), Reopen as draft (cancelled, not started), Restore (archived). */
              ...(() => { const life = lifecycleItems(ev, t, { cancel: () => { setCancelFresh(null); setCancelOpen(true); }, kind: setLifeKind }, true); return life.length ? ["divider" as const, ...life] : []; })(),
              ...((isAdmin() || can("audit.read_object")) ? ["divider" as const, { icon: <Icon.list size={14} />, label: t("events.detailOverlay.viewAuditTrail", "View audit trail"), onClick: () => setAuditOpen(true) }] : []), // #803 — Event Manager (audit.read_object) can see the trail too, not just System Admin
            ]} />
          <IconBtn label={t("common.actions.close", "Close")} onClick={() => nav.closeEvent()}><Icon.x /></IconBtn>
        </div>
      </div>

      <div style={{ display: "flex", gap: 0, padding: phone ? "0 14px" : "0 24px", borderBottom: `1px solid ${T.border}`, overflowX: "auto", flexWrap: phone ? "wrap" : undefined }}>
        <Tab active={tab === "overview"} style={tabStyle} onClick={() => setTab("overview")}>{t("events.detailOverlay.overview", "Overview")}</Tab>
        {/* The badge is the confirmed roster, the same people the "All" filter lists (CC, 2026-10-05, operator ruling). */}
        <Tab active={tab === "guests"} count={ev.confirmed ?? ev.registered} style={tabStyle} onClick={() => setTab("guests")}>{t("events.detailOverlay.registrations", "Registrations")}</Tab>
        <Tab active={tab === "tickets"} count={ticketing.status === "ready" ? activeTickets.length : undefined} style={tabStyle} onClick={() => setTab("tickets")}>{t("events.detailOverlay.tickets", "Tickets")}</Tab>
        <Tab active={tab === "share"} style={tabStyle} onClick={() => setTab("share")}>{t("events.detailOverlay.share", "Share")}</Tab>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: phone ? 14 : 24, display: "flex", flexDirection: "column", gap: 16 }}>
        {tab === "overview" ? <EventOverviewTab ev={ev} tickets={tickets} /> : null}
        {tab === "guests" ? <EventGuestsTab ev={ev} /> : null}
        {tab === "tickets" ? <EventTicketsTab ticketing={ticketing} /> : null}
        {tab === "share" ? <EventShareTab ev={ev} onOpenLanding={() => { nav.closeEvent(); nav.go("landing.editor", { id: ev.id }); }} /> : null}
      </div>
      <CancelEventConfirm ev={cancelOpen ? (cancelFresh ?? ev) : null} nav={nav} knownBooked={!!cancelFresh} onClose={() => { setCancelOpen(false); setCancelFresh(null); }} />
      <EventLifecycleConfirm ev={lifeKind ? ev : null} kind={lifeKind} nav={nav} fromDrawer onClose={() => setLifeKind(null)} onCancelInstead={(fresh) => { setCancelFresh(fresh); setCancelOpen(true); }} />
      {auditOpen ? <AuditTrailDrawer target={{ type: "event", id: ev.id, name: ev.title }} onClose={() => setAuditOpen(false)} /> : null}

      <div style={{ padding: phone ? "12px 14px" : "12px 24px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <Btn variant="secondary" icon={<Icon.send size={14} />} onClick={() => { nav.closeEvent(); nav.go("invite", { eventId: ev.id }); }}>{icu("events.detailOverlay.messageRegistered", "Message {nounPlural}", { nounPlural })}</Btn>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* Without the Customers directory, Edit event is the footer's primary action. */}
          {ev.status === "past" ? null : <Btn variant={customersOff ? "primary" : "secondary"} onClick={() => eventActions.editEvent(ev, nav)}>{t("events.detailOverlay.editEvent", "Edit event")}</Btn>}
          {customersOff ? null : <Btn onClick={() => { nav.closeEvent(); nav.go("customers", { eventId: ev.id }); }}>{icu("events.detailOverlay.openInDirectoryArrow", "Open in {nounPlural} →", { nounPlural })}</Btn>}
        </div>
      </div>
    </>
  );
}

/** The slide-over drawer. Rendered once in App when nav.drawerEvent is set. */
export function EventOverlay({ ev }: { ev: UIEvent }) {
  const t = useT();
  const nav = useNav();
  const phone = useViewport() === "phone";
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") nav.closeEvent(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [nav]);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", justifyContent: "flex-end", background: alpha(T.n1, 40), animation: "evfadeIn 200ms cubic-bezier(0.4,0,0.2,1)" }} onClick={() => nav.closeEvent()}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: phone ? "100%" : 560, maxWidth: phone ? "100%" : "92vw", background: T.white, height: "100%", boxShadow: T.shadowLg, display: "flex", flexDirection: "column", overflow: "hidden", animation: "evslideIn 240ms cubic-bezier(0.4,0,0.2,1)" }}>
        <EventDetailContents ev={ev} />
      </div>
      <style>{t("events.detailOverlay.text3", "@keyframes evfadeIn{from{opacity:0}to{opacity:1}}@keyframes evslideIn{from{transform:translateX(100%)}to{transform:translateX(0)}}")}</style>
    </div>
  );
}
